import { useApolloClient } from '@apollo/react-hooks'
import {
  createStyles,
  withStyles,
  useDebouncedCallback,
  useIsFirstRender,
  useIsMobile,
  AlertDialog,
  Grid,
  Paper,
  SearchBar,
  KanbanColumnsIcon,
  ListIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles, SearchBarProps } from '@paintscout/ui'
import { useClientOptions, useDialogs, useUser, useUserPermissions } from '@paintscout/ui-business'
import { getObjectLabels } from '@paintscout/util/builder'
import { sanitizeFiltersWithAcceptedDate } from '@paintscout/util/util'
import * as Sentry from '@sentry/react'
import { gql } from 'apollo-boost'
import FileSaver from 'file-saver'
import type { ValueFilter } from 'json-to-lucene'
import { jsonToLucene } from 'json-to-lucene'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import classnames from 'classnames'
import { getFiltersFromLocation } from '../QuoteSearchBar'
import DealSearchFilters from './DealSearchFilters'
import DealSearchFilterChips from './DealSearchFilterChips'

export interface DealSearchBarProps
  extends WithStyles<typeof styles>,
    Omit<SearchBarProps, 'onSearch' | 'showFilters' | 'setShowFilters'> {
  loading?: boolean
  invoice?: boolean
  status?: string
  children?: React.ReactNode
  filters?: ValueFilter[]
  onSearch?: (query: string, filters: ValueFilter[], isInitialSearch?: boolean) => any
  hasStage: boolean
  pipeline: string
  view: 'list' | 'board'
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(0, 2)
    },
    rootPadding: {
      paddingBottom: theme.spacing(1)
    },
    searchBarPaper: {
      padding: theme.spacing(0),
      // overflow: 'hidden',
      // paddingBottom: 7,
      border: 'none',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 1)
      }
    },
    sortButtonRoot: {
      height: 42
    },
    sortButtonActive: {
      '&$sortButtonActive': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.main + ' !important'
      }
    }
  })

function DealSearchBar({
  classes,
  invoice,
  loading,
  onSearch,
  status,
  hasStage,
  pipeline,
  view,
  ...props
}: DealSearchBarProps) {
  const location = useLocation<DealUrlQuery>()
  const { options } = useClientOptions()
  const useAcceptedDate = !options?.options?.quotes?.dontCountAcceptedDate
  const apolloClient = useApolloClient()
  const { isSales } = useUser()
  const { permissionToCreateQuote } = useUserPermissions()
  const isFirstRender = useIsFirstRender()
  const mobile = useIsMobile()

  const [showFilters, setShowFilters] = useState(false)

  const [exporting, setExporting] = useState(false)

  const { openDialog, dismissDialog } = useDialogs()

  const dateFormat = options.options.dateFormat.momentValue
  const objectLabels = getObjectLabels({ options, invoice })
  const placeholder = `Search deals by deal name, job address, or value...`

  const urlParams = getUrlQuery<DealUrlQuery>({ location })

  // the normal text of the search straight from the input
  const [queryText, setQueryText] = useState(urlParams.search || '')

  // the committed/current filters for the results
  const [filters, setFilters] = useState(props.filters ?? getFiltersFromLocation(location, true))

  // filters state before the user commits them by hitting search
  const [uncommittedFilters, setUncommittedFilters] = useState(filters)

  const [uncommittedSort, setUncommittedSort] = useState(urlParams.sort || '')

  useEffect(() => {
    if (urlParams.sort) setUncommittedSort(urlParams.sort)
  }, [urlParams.sort])

  // update filters when status tab changes
  useEffect(() => {
    if (!isFirstRender && !filters.find((filter) => filter.id === 'status.value')) {
      handleSearch({
        filters: [
          ...filters,
          {
            id: 'status.value',
            type: 'equal',
            value: status
          }
        ]
      })
    }
    // eslint-disable-next-line
  }, [status])

  // trigger search when props.filters changes
  useEffect(() => {
    if (!isEqual(filters, props.filters)) {
      handleSearch({ filters: props.filters })
    }
    // eslint-disable-next-line
  }, [props.filters])

  // trigger search on mount
  useEffect(() => {
    handleSearch({ filters, query: queryText }, true)
  }, [])

  const handleSearch = (args: { query?: string; filters?: ValueFilter[] } = {}, isInitialSearch?: boolean) => {
    const { query = queryText } = args

    // the filter values (`filters` variable from state is in scope, so we'll call it values)
    const values = [...(args.filters ?? uncommittedFilters)]

    // update filter states
    setFilters(values)
    setUncommittedFilters(values)

    // update the typed-in user text
    setQueryText(query)

    // remove filters with null or undefined values
    const sanitizedValues = values.filter((filter) => {
      // != operator rather than !== is intentional - it checks for null or undefined - update to catch empty arrays
      return (
        filter.value != null &&
        (!Array.isArray(filter.value) || (Array.isArray(filter.value) && filter.value.filter(Boolean).length))
      )
    })

    if (uncommittedSort) {
      localStorage.setItem('boardViewSort', JSON.stringify(uncommittedSort))
    }
    setUrlQuery({
      location,
      params: { search: query || null, filters: sanitizedValues, ...(uncommittedSort && { sort: uncommittedSort }) }
      // replaceParams: true
    })

    onSearch(query, sanitizedValues, isInitialSearch)
  }

  const debouncedSearch = useDebouncedCallback(handleSearch, 100)

  const handleFilterClear = () => {
    setUncommittedFilters([])
  }

  const handleExport = async () => {
    const dateFilter = uncommittedFilters.find((filter) => filter.id === 'created')
    const fileName = dateFilter
      ? // "Deals 01/01/2000 - 02/01/2000"
        `Deals ${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(dateFilter.value[1]).format(
          dateFormat
        )}.csv`
      : // "Deals <today's date>"
        `Deals-${moment(Date.now()).format(dateFormat)}.csv`

    setExporting(true)
    try {
      // use uncommitted filters since search box will still be open
      const withTrashedFilter: ValueFilter[] = [
        ...uncommittedFilters,
        { type: 'equal', id: 'trashed', value: true, not: true }
      ]
      const luceneQuery = jsonToLucene({
        query: queryText,
        filters: useAcceptedDate ? sanitizeFiltersWithAcceptedDate(withTrashedFilter) : withTrashedFilter
      })

      const sanitizedQuery = luceneQuery ? luceneQuery : '*:*'
      const query = sanitizedQuery.replace(/\s*AND\s*\(\)/g, '')
      const { data } = await apolloClient.query({
        query: gql`
          query ($query: String!, $sort: [String!]) {
            exportDeals(query: $query, sort: $sort) {
              csv
              rows
            }
          }
        `,
        variables: {
          query,
          // Need 2 sort params for tiebreaker, keep sort from quoteList if present else same default
          sort: ['-created', '-updated']
        }
      })

      if (data.exportDeals.rows) {
        FileSaver.saveAs(new Blob([data.exportDeals.csv]), fileName)
      } else {
        openDialog(AlertDialog, {
          message: 'No rows were found to export for this search query.',
          onConfirm: () => {
            dismissDialog()
          }
        })
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    setExporting(false)
  }

  function deleteFilter({ id, valueIndex }: { id: string; valueIndex?: number }) {
    if (valueIndex >= 0 && (id === 'stage.value' || id === 'owner.id' || id === 'quoteType' || id === 'tags')) {
      handleSearch({
        filters: filters
          .map((filter) => {
            if (filter.id === id && filter?.value) {
              if (filter.type === 'in' || filter.id === 'tags') {
                return {
                  ...filter,
                  value: (filter.value as any).filter((value, index) => index !== valueIndex)
                }
              } else {
                return null
              }
            } else {
              return filter
            }
          })
          .filter(Boolean)
      })
    } else {
      // Remove the only filter of type
      handleSearch({
        filters: filters.filter((filter) => {
          return filter.id !== id
        })
      })
    }
  }

  // TODO: ToggleButtonGroup should be a ui component
  const history = useHistory()

  const [page, setPage] = useState(view || 'board')

  const handlePageChange = () => {
    const newPage: 'board' | 'list' = page === 'board' ? 'list' : 'board'
    setPage(newPage)
    history.push(`/deals/${newPage}`)
  }

  return (
    <Paper className={classnames({ [classes.root]: true, [classes.rootPadding]: showFilters })}>
      <Grid container spacing={0} alignItems="flex-start">
        <Grid item xs>
          <SearchBar
            classes={{ paper: classes.searchBarPaper }}
            inputProps={{
              'aria-label': `Search ${objectLabels.quote.plural}`,
              // recommended aria for loading indicator (also used in e2e tests)
              'aria-busy': loading,
              'aria-live': loading ? 'polite' : undefined
            }}
            filterMenuCollapse={!mobile}
            defaultValue={queryText}
            showExport={permissionToCreateQuote && !isSales}
            placeholder={placeholder}
            isExporting={exporting}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            startAdornment={
              <Grid item xs={'auto'} style={{ marginRight: 12 }}>
                <ToggleButtonGroup
                  value={page}
                  exclusive
                  onChange={handlePageChange}
                  aria-label="text alignment"
                  style={{ borderRadius: 4 }}
                >
                  <ToggleButton
                    value="board"
                    classes={{
                      root: classnames(classes.sortButtonRoot, { [classes.sortButtonActive]: page === 'board' })
                    }}
                  >
                    <KanbanColumnsIcon />
                  </ToggleButton>
                  <ToggleButton
                    value="list"
                    classes={{
                      root: classnames(classes.sortButtonRoot, { [classes.sortButtonActive]: page === 'list' })
                    }}
                  >
                    <ListIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            }
            filterContent={
              <DealSearchFilters
                view={view}
                pipeline={pipeline}
                invoice={invoice}
                filters={uncommittedFilters}
                onChange={setUncommittedFilters}
                sort={uncommittedSort}
                onChangeSort={setUncommittedSort}
                sortOptions={sortOptions}
                hasStage={hasStage}
              />
            }
            onClear={handleFilterClear}
            onExport={handleExport}
            onChange={(q) => {
              const cleanQuery = q.replace(/\\/g, '/')
              debouncedSearch({ query: cleanQuery })
            }}
            onSearch={(q) => handleSearch({ query: q })}
            {...props}
          />
        </Grid>
      </Grid>
      <DealSearchFilterChips
        pipeline={pipeline}
        show={(filters ?? []).filter((filter) => filter.id != 'is_invoice').length > 0 && !showFilters}
        filters={filters}
        onDelete={(filter, valueIndex) => deleteFilter({ id: filter.id, valueIndex })}
      />
    </Paper>
  )
}

export default withStyles(styles)(DealSearchBar)

export interface DealUrlQuery {
  sort?: string
  search?: string
  filters: ValueFilter[]
  page?: number
  pipeline?: string
  bookmark?: string
  fullLoad?: boolean
}

export const sortOptions = [
  { label: 'Deal Name', value: 'name' },
  { label: 'Date Created', value: 'date' },
  { label: 'Time in Stage', value: 'enteredStage' },
  { label: 'Owner', value: 'owner' },
  { label: 'Value', value: 'value' },
  { label: 'Lead Source', value: 'lead_source' }
]
